import {
  isDefined,
  isNotDefined,
  isRoleId,
  isTeamId,
  isUserId,
  type DashboardDataWithEditable,
  type DashboardFilter,
  type DashboardId,
  type DashboardScopeId,
  type DashboardWidget,
  type DashboardWidgetId,
  type UserId,
} from '@terros/common'

export type HustleDashboards = {
  /** The scope's own hustle dashboard, if it has built one. */
  own: DashboardDataWithEditable | undefined
  /**
   * Hustle dashboards reaching this scope from something broader — a team, a
   * role, the company — ordered most specific first. A viewer can be in range of
   * several at once (their team's *and* their company's), so all are listed.
   */
  inherited: DashboardDataWithEditable[]
  /** The one Home actually shows: the most specific of own and inherited. */
  surfacedDashboardId: DashboardId | undefined
}

/**
 * Splits the hustle dashboards in range by whose they are, and works out which
 * one Home surfaces. Home shows exactly one hustle widget — the most specific
 * that reaches the viewer: their own, then their team's, then their role's, then
 * the company's.
 */
export function getHustleDashboards({
  dashboards,
  scopeEntityId,
  viewerUserId,
}: {
  dashboards: DashboardDataWithEditable[]
  /** The scope being viewed, which is the viewer themselves by default. */
  scopeEntityId: DashboardScopeId | undefined
  viewerUserId: UserId | undefined
}): HustleDashboards {
  const scope = scopeEntityId ?? viewerUserId
  const byScope = dashboards
    .filter((dashboard) => dashboard.home === true)
    .sort((left, right) => compareHustleDashboards(left, right, scope))

  return {
    own: byScope.find((dashboard) => dashboard.scopeId === scope),
    inherited: byScope.filter((dashboard) => dashboard.scopeId !== scope),
    surfacedDashboardId: byScope[0]?.dashboardId,
  }
}

/**
 * The data filter a hustle dashboard should report under, given the scope it
 * belongs to: the viewer's own numbers when the hustle is theirs, otherwise the
 * entity it is scoped to.
 *
 * Company scope has no `fixed` representation — the fixed filter covers teams,
 * users and roles only — so it falls through to `undefined`, which reads as
 * everything the viewer can see and is the company-wide population anyway.
 */
export function getHustleFilterForScope({
  scopeEntityId,
  viewerUserId,
}: {
  scopeEntityId: DashboardScopeId | undefined
  viewerUserId: UserId | undefined
}): DashboardFilter | undefined {
  if (isNotDefined(scopeEntityId)) {
    // current_user with no viewer to resolve against would be a lie.
    return isDefined(viewerUserId) ? { type: 'current_user' } : undefined
  }
  if (scopeEntityId === viewerUserId) return { type: 'current_user' }
  if (isTeamId(scopeEntityId)) return { type: 'fixed', filter: { teams: [scopeEntityId] } }
  if (isUserId(scopeEntityId)) return { type: 'fixed', filter: { users: [scopeEntityId] } }
  if (isRoleId(scopeEntityId)) return { type: 'fixed', filter: { roles: [scopeEntityId] } }
  return undefined
}

/**
 * A copy of a handed-down hustle widget, ready to become the viewer's own.
 *
 * The widget's own filter is dropped so it inherits the dashboard's, and the
 * viewer's existing widget name wins: the hustle editor hides the widget name
 * field, so a source widget usually has none and taking its name would wipe
 * whatever the viewer called theirs.
 */
export function buildHustleWidgetCopy({
  source,
  widgetId,
  existingName,
}: {
  source: DashboardWidget
  widgetId: DashboardWidgetId
  existingName: string | undefined
}): DashboardWidget {
  return { ...source, widgetId, filter: undefined, name: existingName ?? source.name }
}

/** Lower sorts first: own, then team, then role, then company or anything else. */
export function getHustleScopeRank(scopeId: DashboardScopeId, scopeEntityId: DashboardScopeId | undefined): number {
  if (isDefined(scopeEntityId) && scopeId === scopeEntityId) return 0
  if (isTeamId(scopeId)) return 1
  if (isRoleId(scopeId)) return 2
  return 3
}

function compareHustleDashboards(
  left: DashboardDataWithEditable,
  right: DashboardDataWithEditable,
  scopeEntityId: DashboardScopeId | undefined
): number {
  const rankDelta = getHustleScopeRank(left.scopeId, scopeEntityId) - getHustleScopeRank(right.scopeId, scopeEntityId)
  if (rankDelta !== 0) return rankDelta

  const nameDelta = (left.name ?? '').localeCompare(right.name ?? '', undefined, { sensitivity: 'base' })
  if (nameDelta !== 0) return nameDelta
  return left.dashboardId.localeCompare(right.dashboardId)
}
